import { JSX } from 'react';
import { BillingAddonCode } from './billing-addon';
import { ManageMembersContext, ManageMembersOrgLimits, ManageMembersType, ManageMembersView } from './manage-members';
import { MenuItem, MenuItemType, MenuType } from './menu';
import { OrgInfoSubscriptionType } from './org-info';
import { PortalStyleConfig } from './portal-theme';
import { ThemeColorSchemas } from './theme-color-schemas';
import { TranscriptUsage } from './transcript';

export enum PopupType {
  INVITE = 'invite',
  CONFIRM = 'confirm',
  RENAME = 'rename-node',
  DELETE = 'delete',
  MANAGE = 'manage',
  EMOJI = 'emoji',
  TEXT_FROM_IMAGE = 'text-from-image',
  PAYWALL = 'paywall',
  SHARE = 'share',
  BUY_PORTAL = 'buy-portal',
  TRIAL_WELCOME = 'trial-welcome',
  FROM_CALL = 'from-call',
  FROM_CALL_END = 'from-call-end',
  UNLOCK_AI = 'unlock-ai',
  UNLOCK_AI_BILLING = 'unlock-ai-billing',
  /**
   * arbitrary dialog with specfic title, text and buttons
   */
  DIALOG = 'dialog',
  CUSTOMIZER_UNPUBLISHED_CHANGES = 'customizer-unpublished-changes',
  PAGE_SETTING_IN_PORTAL = 'page-setting-in-portal',
  ADD_PAGE_TO_PORTAL = 'add-page-to-portal',
  EDIT_PORTAL_PAGE = 'edit-portal-page',
  EDIT_PORTAL_LINK = 'edit-portal-link',
  FILE_INFO = 'file-info',
  SUCCESS_MOVE_COPY = 'success-move-copy',
  MANAGE_MEMBERS = 'manage-members',
  PUBLISH_INFORM = 'publish-inform',
  TEMPLATE_PAGE = 'template-page',
  TRANSCRIPT_LIMIT_LTD = 'transcript-limit-ltd',
  AUTOMATION_LIMIT_LTD = 'automation-limit-ltd',
  GET_TRANSCRIBE = 'get-transcribe',
  PURCHASE_TRANSCRIBE_BILLING = 'purchase-transcribe-billing',
  WIZARD_EXIT = 'wizard-exit',
  WIZARD_EXIT_THANKS = 'wizard-exit-thanks',
  GET_AUTOMATION = 'get-automation',
  PURCHASE_AUTOMATION_BILLING = 'purchase-automation-billing',
  ADDON = 'addon',
  TRANSCRIPT_AD = 'transcript-ad',
  CREATE_PORTAL_ENTITY = 'create-portal-page',
  CREATE_PORTAL_LINK = 'create-portal-link',
  PROCESS_VIEW_INTRO_POPUP = 'process-view_intro_popup',
  PORTAL_IS_READY = 'portal-is-ready',
  DELETE_PROJECTION_ORIGIN = 'delete-projection-origin',
}

export enum PaywallPopupReason {
  BOTTOM_NAVBAR_UPGRADE = 'bottom_navbar_upgrade',
  MENU_UPGRADE_TO_PRO = 'menu_upgrade_to_pro',
  NOTES_LIMIT = 'notes_limit',
  ATTACHMENT_LIMIT = 'attachment_limit',
  BOTTOM_MENU_TRIAL = 'bottom_menu_trial',
  BOTTOM_MENU_UPGRADE = 'bottom_menu_upgrade',
  LOCK_BLOTS = 'lock_blots',
  REC_DURATION_LIMIT = 'rec_duration_limit',
  NOTE_REVISIONS_POPUP = 'note_revisions_popup',
  BUSINESS_PLAN_NEEDED = 'business_plan_needed',
  FROM_BACKUP = 'from_backup',
  SERVER_BACKUPS = 'server_backups',
  TWO_STEP_VERIFICATION = 'two_step_verification',
  SET_PAGE_PASSWORD = 'set_page_password',
  DUPLICATE_FOLDER = 'duplicate_folder',
  SETTINGS_SUB_GO_TO = 'settings_sub_go_to',
  SETTINGS_SUB_BLOCKS = 'settings_sub_blocks',
  SETTINGS_SUB_NOTES = 'settings_sub_notes',
  WORKSPACES_LIMIT = 'workspaces_limit',
  BLOCKS_LIMIT = 'blocks_limit',
  AI_LIMIT = 'ai_limit',
  MANAGE_MEMBERS_GRANULAR = 'manage_members_granular',
  PORTAL_MANAGE_ACCESS_GRANULAR = 'portal_manage_access_granular',
  BRANDING = 'branding',
  INTEGRATIONS = 'integrations',
  DOWNLOAD_PDF = 'download_pdf',
  TRANSCRIBE = 'transcribe',
  WINDOW_EVENT = 'window_event',
  AUTOMATION = 'automation',
  AI_ASSISTANT = 'ai_assistant',
  PORTAL = 'portal',
  CNAME = 'cname',
  STORAGET_SIZE_LIMIT = 'storage_size_limit',
  TRAFFIC_LIMIT = 'traffic_limit',
  FILE_SIZE_LIMIT = 'file_size_limit',
  OCR = 'ocr',
  TRIAL_WELCOME = 'trial_welcome',
}

export enum PaywallPopupPricingReason {
  PREMIUM_BANNER_NOTES = 'premium_banner_notes',
  PREMIUM_BANNER_LIMIT100MB = 'premium_banner_limit100mb',
  PREMIUM_BANNER_ATTACHMENT10MB = 'premium_banner_attachment10mb',
  BOTTOM_MENU_UPGRADE = 'bottom_menu_upgrade',
  PREMIUM_BANNER_PDF = 'premium_banner_pdf',
  PREMIUM_BANNER_REC_DURATION_LIMIT = 'premium_banner_rec_duration_limit',
  NOTE_REVISIONS_POPUP = 'note_revisions_popup',
  PREMIUM_BANNER_LIMIT_WS = 'premium_banner_limit_ws',
  PREMIUM_BANNER_BACKUP = 'premium_banner_backup',
  PREMIUM_BANNER_VERIFICATION = 'premium_banner_verification',
  SET_PAGE_PASSWORD = 'set_page_password',
  MENU_UPGRADE_TO_PRO = 'menu_upgrade_to_pro',
  PREMIUM_BANNER_DUPLICATE_FOLDER = 'premium_banner_duplicate_folder',
  SETTINGS_SUB_GO_TO = 'settings_sub_go_to',
  PREMIUM_BANNER_SETTINGS_SUB_BLOCKS = 'premium_banner_settings_sub_blocks',
  PREMIUM_BANNER_SETTINGS_SUB_NOTES = 'premium_banner_settings_sub_notes',
  BLOCKS_LIMIT = 'blocks_limit',
  WINDOW_EVENT = 'window_event',
}

type PageWidgetActionType =
  | 'fusebaseAi'
  | 'board'
  | 'tasks'
  | 'table'
  | 'click'
  | 'client-brief'
  | 'competitor-analysis'
  | 'customer-testimonials';

export interface PaywallPopupPayload {
  payloadType: PopupType.PAYWALL;
  mode?: 'upgrade' | 'trialStart' | 'trialEnd' | 'addons';
  title?: string;
  reason?: PaywallPopupReason;
  returnUrl?: string;
  pricingReason?: PaywallPopupPricingReason;
  orgId?: string;
  showBanner?: boolean;
  subscriptionType?: OrgInfoSubscriptionType;
  escapeToClose?: boolean;
  appName?: string;
  coupon?: string;
}

export interface SharePopupPayload {
  payloadType: PopupType.SHARE;
  workspaceId?: string;
  globalId: string;
}

export interface BuyPortalPopupPayload {
  payloadType: PopupType.BUY_PORTAL;
  workspaceId?: string;
  orgId?: string;
  createWorkspace?: {
    shouldByPortalAddon: boolean;
    title: string;
    orgId: string;
  };
}

export interface TrialWelcomePopupPayload {
  payloadType: PopupType.TRIAL_WELCOME;
  animated?: boolean;
}

export interface FromCallPopupPayload {
  payloadType: PopupType.FROM_CALL;
}

export interface FromCallEndPopupPayload {
  payloadType: PopupType.FROM_CALL_END;
  orgId: string;
}

export interface UnlockAIPopupPayload {
  payloadType: PopupType.UNLOCK_AI;
  usage?: { max: number; current: number };
}

export interface UnlockAIBillingPopupPayload {
  payloadType: PopupType.UNLOCK_AI_BILLING;
}

export interface GetTranscribePopupPayload {
  payloadType: PopupType.GET_TRANSCRIBE;
  usage: TranscriptUsage;
  isAddonExists: boolean;
}

export interface PurchaseTranscribeBillingPopupPayload {
  payloadType: PopupType.PURCHASE_TRANSCRIBE_BILLING;
}

export interface GetAutomationPopupPayload {
  payloadType: PopupType.GET_AUTOMATION;
  usage?: { max: number; current: number };
}

export interface PurchaseAutomationBillingPopupPayload {
  payloadType: PopupType.PURCHASE_AUTOMATION_BILLING;
}

export interface ManagePopupPayload {
  payloadType: PopupType.MANAGE;
  subjectType: 'workspace' | 'note' | 'folder';
  globalId?: string;
  parentId?: string;
  title?: string;
  isDefaultOrInside?: boolean;
}

export interface InvitePopupPayload extends Omit<ManagePopupPayload, 'payloadType'> {
  payloadType: PopupType.INVITE;
  backToManage?: ManagePopupPayload;
  skipInviteRedirect?: boolean;
}

export interface RenamePopupPayload {
  payloadType: PopupType.RENAME;
  type: string;
  value: string;
  message?: string;
  cancelButtonCustomClasses?: string;
  confirmButtonCustomClasses?: string;
  onApply: (value: string) => void;
  themeColors?: ThemeColorSchemas;
  themeStyle?: PortalStyleConfig;
}

interface Template {
  name: string;
  page: unknown[];
}

export interface TemplatePagePayload {
  payloadType: PopupType.TEMPLATE_PAGE;
  onApply: (action: PageWidgetActionType, templates?: Template[]) => Promise<void>;
  globalId?: string;
  parentId?: string;
}

export interface EmojiPopupPayload {
  payloadType: PopupType.EMOJI;
  allowRemove?: boolean;
  onApply: (value: string) => void;
}

export interface TextFromImagePayload {
  payloadType: PopupType.TEXT_FROM_IMAGE;
  isFeatureAvailable: boolean;
  workspaceId: string;
  attachmentId: string;
}

export interface CustomizerUnpublishedChangesPopupPayload {
  payloadType: PopupType.CUSTOMIZER_UNPUBLISHED_CHANGES;
  text: string | JSX.Element;
  themeColors?: ThemeColorSchemas;
  themeStyle?: PortalStyleConfig;
  onClose?: () => void;
  buttons: {
    text: string;
    main?: boolean;
    classes?: string[];
    size?: string; // BUTTON_SIZE
    /**
     * What buttons does on click. If not specified - closes dialog.
     * @returns
     */
    onClick?: () => void;
  }[];
}
export interface DialogPopupPayload {
  payloadType: PopupType.DIALOG;
  text: string | JSX.Element;
  themeColors?: ThemeColorSchemas;
  themeStyle?: PortalStyleConfig;
  onClose?: () => void;
  buttons: {
    text: string;
    main?: boolean;
    classes?: string[];
    size?: string; // BUTTON_SIZE
    /**
     * What buttons does on click. If not specified - closes dialog.
     * @returns
     */
    onClick?: () => void;
  }[];
}

export interface PageSettingInPortalPayload {
  payloadType: PopupType.PAGE_SETTING_IN_PORTAL;
  workspaceId: string;
  globalId: string;
}

export interface AddPageToPortalPayload {
  payloadType: PopupType.ADD_PAGE_TO_PORTAL;
  workspaceId: string;
  globalId: string;
}

export interface CreatePortalEntityPayload {
  payloadType: PopupType.CREATE_PORTAL_ENTITY;
  entityType: 'page' | 'folder' | 'process';
  menuType: MenuType;
  parentId?: string;
}

export interface CreatePortalLinkPayload {
  payloadType: PopupType.CREATE_PORTAL_LINK;
  menuType: MenuType;
  parentId?: string;
}

export interface EditPortalPagePayload {
  payloadType: PopupType.EDIT_PORTAL_PAGE;
  name: string;
  url: string;
  type: 'page' | 'folder';
  showUrl?: boolean;
  onWantToClose: () => void;
  onSave: (name: string, url: string) => void;
}

export interface EditPortalLinkPayload {
  payloadType: PopupType.EDIT_PORTAL_LINK;
  name: string;
  url: string;
  openInNewTab: boolean;
  onWantToClose: () => void;
  onSave: (name: string, url: string, openInNewTab: boolean) => void;
}

export interface FileInfoPayload {
  payloadType: PopupType.FILE_INFO;
  themeColors: ThemeColorSchemas;
  themeStyle: PortalStyleConfig;
  fileId: string;
  fileOpenHost: string;
  orgRole?: string;
  userId?: number;
  openFileLocationEnabled?: boolean;
  canEditOtherUsersFile?: boolean;
  deleteDisabled?: boolean;
  renameDisabled?: boolean;
}
export interface SuccessMoveCopyPayload {
  payloadType: PopupType.SUCCESS_MOVE_COPY;
  workspaceId: string;
  noteId: string;
}

export interface ManageMembersPayload {
  payloadType: PopupType.MANAGE_MEMBERS;
  data: {
    type: ManageMembersType;
    initialViewMode: ManageMembersView;
    orgMaxMembers: number;
    context: ManageMembersContext;
    menuItem?: MenuItem;
    orgLimits: ManageMembersOrgLimits;
    forceIsOnlySharedAvailable?: boolean;
    onClose: () => void;
  };
}

export interface PublishInformPayload {
  payloadType: PopupType.PUBLISH_INFORM;
  data: {
    isVisible: boolean;
    nodeName: string;
    onSubmit: () => void;
  };
}

export interface WizardExitPopupPayload {
  payloadType: PopupType.WIZARD_EXIT;
  onSubmit: (value: string, otherValue?: string) => void;
}

export interface AddonPopupPayload {
  payloadType: PopupType.ADDON;
  code: BillingAddonCode;
}

export interface TranscriptADPopupPayload {
  payloadType: PopupType.TRANSCRIPT_AD;
  onClose: () => void;
  onTranscriptRequest: () => void;
}

export interface ProcessViewIntroPopupPayload {
  payloadType: PopupType.PROCESS_VIEW_INTRO_POPUP;
  menuType: MenuType;
  parentId?: string;
}

export interface DeleteProjectionOriginPopupPayload {
  payloadType: PopupType.DELETE_PROJECTION_ORIGIN;
  menuItemId: string;
  menuItemType: MenuItemType;
  submit: (removeOrigin: boolean) => void;
}

export interface PortalIsReady {
  payloadType: PopupType.PORTAL_IS_READY;
}

export type PopupPayload =
  | ManagePopupPayload
  | InvitePopupPayload
  | RenamePopupPayload
  | EmojiPopupPayload
  | PaywallPopupPayload
  | UnlockAIPopupPayload
  | UnlockAIBillingPopupPayload
  | SharePopupPayload
  | BuyPortalPopupPayload
  | TrialWelcomePopupPayload
  | FromCallPopupPayload
  | FromCallEndPopupPayload
  | TextFromImagePayload
  | DialogPopupPayload
  | CustomizerUnpublishedChangesPopupPayload
  | PageSettingInPortalPayload
  | AddPageToPortalPayload
  | EditPortalPagePayload
  | EditPortalLinkPayload
  | FileInfoPayload
  | SuccessMoveCopyPayload
  | ManageMembersPayload
  | PublishInformPayload
  | TemplatePagePayload
  | GetTranscribePopupPayload
  | PurchaseTranscribeBillingPopupPayload
  | WizardExitPopupPayload
  | GetAutomationPopupPayload
  | PurchaseAutomationBillingPopupPayload
  | AddonPopupPayload
  | TranscriptADPopupPayload
  | CreatePortalEntityPayload
  | CreatePortalLinkPayload
  | ProcessViewIntroPopupPayload
  | DeleteProjectionOriginPopupPayload;
